import { useCallback, useEffect, useMemo, useState } from 'react'

import { TextLink, useToast } from '@circlefin/components'
import { settlementAdvanceDetailSection } from '@services/sections/lib/settlementAdvance'
import { tokenToCurrency } from '@shared/components/common/FxTransactionCard/helpers'
import { GraphQLErrorBoundary } from '@shared/components/errors/GraphQLErrorBoundary/GraphQLErrorBoundary'
import {
  SettlementCreditLinesDocument,
  SettlementCreditTransferDocument,
  SettlementCreditTransferStatus,
  useCancelReservedFundsMutation,
  useCreateSettlementCreditTransferMutation,
  useSettlementCreditTransfersQuery,
  useUpdateReservedFundsProofMutation,
} from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { CountdownProvider } from '../countdown/provider'

import { STEPS, FloatingLimitsContext } from './context'

import type {
  FloatingLimitsActions,
  FloatingLimitsState,
  ReserveAmountStepFormValues,
  Step,
  UploadProofStepFormValues,
} from './context'
import type { SettlementCreditLine } from '@shared/graphql'

interface FloatingLimitsProviderProps {
  /**
   * React Node Children.
   */
  children?: React.ReactNode
  /**
   * Settlement Credit Line.
   */
  settlementCreditLine?: SettlementCreditLine
}

export const FloatingLimitsProvider: React.FC<FloatingLimitsProviderProps> = ({
  children,
  settlementCreditLine,
}) => {
  const { t } = useTranslation('settlementAdvance')
  const { success } = useToast()
  const router = useRouter()

  const [currentStep, setCurrentStep] = useState<Step>(
    settlementCreditLine?.fundsReserved ? 'INSTRUCTIONS' : 'EXPLAINER',
  )

  useEffect(() => {
    if (settlementCreditLine?.fundsReserved) {
      setCurrentStep('INSTRUCTIONS')
    }
  }, [settlementCreditLine])

  const [
    uploadProof,
    { error: uploadProofError, loading: uploadProofLoading },
  ] = useUpdateReservedFundsProofMutation()
  const [cancelTransfer, { error: cancelError, loading: cancelLoading }] =
    useCancelReservedFundsMutation()

  const [
    createTransfer,
    {
      data: currentTransfer,
      error: transferError,
      loading: transferLoading,
      reset: resetTransfer,
    },
  ] = useCreateSettlementCreditTransferMutation()

  const {
    data: reservedTransfers,
    error: reservedTransfersError,
    refetch: reservedTransfersRefetch,
  } = useSettlementCreditTransfersQuery({
    variables: {
      filter: {
        lineId: settlementCreditLine?.id,
        status: SettlementCreditTransferStatus.funds_reserved,
      },
      page: {
        pageSize: 10,
      },
    },
    skip:
      !settlementCreditLine?.id ||
      !settlementCreditLine.fundsReserved ||
      !!currentTransfer,
  })

  const reservedTransfer = useMemo(() => {
    return (
      currentTransfer?.createSettlementCreditTransfer ??
      reservedTransfers?.settlementCreditTransfers.list[0]
    )
  }, [
    currentTransfer?.createSettlementCreditTransfer,
    reservedTransfers?.settlementCreditTransfers.list,
  ])

  const handleViewRequest = useCallback(
    (id: string) => () => {
      void router.push({
        pathname: settlementAdvanceDetailSection.route,
        query: {
          id: id,
        },
      })
    },
    [router],
  )

  const showCancelToast = useCallback(
    (id: string) => {
      success(
        <span className="flex gap-1">
          <span>{t('request.cancelled')}</span>
          <TextLink
            className="text-white"
            onClick={handleViewRequest(id)}
            variant="primary"
          >
            {t('request.viewRequest')}
          </TextLink>
        </span>,
      )
    },
    [handleViewRequest, success, t],
  )

  const showSuccessToast = useCallback(
    (id: string, message: string) => {
      success(
        <span className="flex gap-1">
          <span>{message}</span>
          <TextLink
            className="text-white"
            onClick={handleViewRequest(id)}
            variant="primary"
          >
            {t('request.viewRequest')}
          </TextLink>
        </span>,
      )
    },
    [handleViewRequest, success, t],
  )

  const onNextStep = useCallback(() => {
    setCurrentStep(STEPS[STEPS.indexOf(currentStep) + 1])
  }, [currentStep])

  const onStartOver = useCallback(() => {
    setCurrentStep('RESERVE')
  }, [])

  const onCancel = useCallback(() => {
    if (!settlementCreditLine || !reservedTransfer) return

    void cancelTransfer({
      variables: {
        id: reservedTransfer.id,
      },
      refetchQueries: [
        SettlementCreditLinesDocument,
        SettlementCreditTransferDocument,
      ],
      onError: () => {
        // Cancel error handled by GraphQLErrorBoundary
      },
      onCompleted: (data) => {
        showCancelToast(data.cancelReservedFunds.id)
        onStartOver()
        resetTransfer()
      },
    })
  }, [
    settlementCreditLine,
    reservedTransfer,
    cancelTransfer,
    showCancelToast,
    onStartOver,
    resetTransfer,
  ])

  const onTransfer = useCallback(
    (values: ReserveAmountStepFormValues, callback?: () => void) => {
      if (!settlementCreditLine) return

      void createTransfer({
        variables: {
          input: {
            amount: {
              amount: values.amount.toString(),
              currency: tokenToCurrency(settlementCreditLine.limit.currency),
            },
            settlementCreditLineId: settlementCreditLine.id,
            floatingLimitEnabled: true,
          },
        },
        refetchQueries: [SettlementCreditLinesDocument],
        onError: () => {
          // Transfer error handled by GraphQLErrorBoundary
          // TODO: Add logic to display available limit changed banner
        },
        onCompleted: () => {
          onNextStep()
          callback?.()
        },
      })
    },
    [createTransfer, onNextStep, settlementCreditLine],
  )

  const onUploadProof = useCallback(
    async (values: UploadProofStepFormValues, callback?: () => void) => {
      if (!settlementCreditLine || !reservedTransfer) return

      await uploadProof({
        variables: {
          input: {
            transferId: reservedTransfer.id,
            fileContent: values.evidence,
            fileName: values.evidence.name,
          },
        },
        refetchQueries: [
          SettlementCreditLinesDocument,
          SettlementCreditTransferDocument,
        ],
        onError: () => {
          // Upload error handled by GraphQLErrorBoundary
        },
        onCompleted: (data) => {
          showSuccessToast(
            data.updateReservedFundsProof.id,
            t('request.success'),
          )
          onStartOver()
          callback?.()
        },
      })
    },
    [
      reservedTransfer,
      onStartOver,
      settlementCreditLine,
      showSuccessToast,
      t,
      uploadProof,
    ],
  )

  const context: FloatingLimitsState = useMemo(() => {
    return {
      error: transferError || cancelError || uploadProofError,
      loading: transferLoading || cancelLoading || uploadProofLoading,
      settlementCreditTransfer: reservedTransfer,
      currentStep,
    }
  }, [
    transferError,
    cancelError,
    uploadProofError,
    transferLoading,
    cancelLoading,
    uploadProofLoading,
    reservedTransfer,
    currentStep,
  ])

  const actions: FloatingLimitsActions = useMemo(() => {
    return {
      onCancel,
      onNextStep,
      onStartOver,
      onTransfer,
      onUploadProof,
    }
  }, [onCancel, onNextStep, onStartOver, onTransfer, onUploadProof])

  return (
    <FloatingLimitsContext.Provider value={[context, actions]}>
      <CountdownProvider expireAt={reservedTransfer?.expireAt}>
        <GraphQLErrorBoundary
          error={reservedTransfersError}
          retry={reservedTransfersRefetch}
        >
          {children}
        </GraphQLErrorBoundary>
      </CountdownProvider>
    </FloatingLimitsContext.Provider>
  )
}
